import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useProductCodes } from '../context/ProductCodesContext';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import {
  Plus, X, CheckCircle2, AlertCircle, AlertTriangle, ChevronDown, ChevronRight, Loader2,
  FlaskConical, LayoutGrid, List, Calendar, Layers, Wrench, Trash2, Save,
  Package, Clock, ArrowRight, ArrowLeft, Edit3, RefreshCw, Search, History,
  Printer,
} from 'lucide-react';
import SubscribeButton from '../components/SubscribeButton';
import ProtectedView from '../components/ProtectedView';
import CodeLabel from '../components/CodeLabel';
import MobileModal from '../components/MobileModal';
import ItemCodeLabel from '../components/ItemCodeLabel';
import TimingEditor from '../components/production/TimingEditor';
import RecipeVersionsModal from '../components/production/RecipeVersionsModal';
import BatchSheetModal from '../components/production/BatchSheetModal';
import BatchGroupDetailModal from '../components/BatchGroupDetailModal';
import ProductCodeBadge from '../components/ProductCodeBadge';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const BATCH_STATUSES = ['Planned', 'In Production', 'QC', 'Packing', 'Completed'];
const BATCH_STYLES = {
  'Planned':       'bg-zinc-800 text-zinc-300 border-zinc-700',
  'In Production': 'bg-amber-900/40 text-amber-300 border-amber-700',
  'QC':            'bg-blue-900/40 text-blue-300 border-blue-700',
  'Packing':       'bg-purple-900/40 text-purple-300 border-purple-700',
  'Completed':     'bg-green-900/40 text-green-300 border-green-700',
};

// A recipe line in pieces / boxes / bags (or tagged "packaging") is consumed with the batch but is never product weight.
const COUNT_UNITS = new Set(['pcs', 'pc', 'piece', 'pieces', 'nos', 'no', 'unit', 'units', 'pack', 'packs', 'packet', 'packets', 'box', 'boxes', 'btl', 'bottle', 'bottles', 'roll', 'rolls', 'bag', 'bags', 'carton', 'cartons', 'jar', 'jars']);
const isPackagingLine = (m) => (m.phase || 'premix') === 'packaging' || COUNT_UNITS.has(String(m.unit || 'kg').trim().toLowerCase());
const weightOf = (m) => (isPackagingLine(m) ? 0 : (parseFloat(m.quantity_per_batch || m.quantity_required) || 0));


export default function Production() {
  const { user } = useAuth();
  // iter217 — STRICT cost visibility. Only admin/owner sees batch costs.
  const canSeeCost = user?.role === 'admin' || user?.role === 'owner';
  const navigate = useNavigate();
  const { productMap: productCodesMap, lookup: lookupItemCode } = useProductCodes();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [tab, setTab] = useState('kanban');
  const [batches, setBatches] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [prodLines, setProdLines] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [editRecipe, setEditRecipe] = useState(null);
  const [showVersionsRecipe, setShowVersionsRecipe] = useState(null);
  // Recipe search & filter
  const [recipeSearch, setRecipeSearch] = useState('');
  const [recipeFilter, setRecipeFilter] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [batchSheetBatchId, setBatchSheetBatchId] = useState(null);
  const [expandedDate, setExpandedDate] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  // Filters
  const [lineFilter, setLineFilter] = useState('All');
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [productFilter, setProductFilter] = useState('');
  const [batchSearch, setBatchSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [openBatch, setOpenBatch] = useState(null);
  // Timing editor
  const [editTimings, setEditTimings] = useState(null);
  // History
  const [historyBatches, setHistoryBatches] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // iter217 — Overhead allocation (Phase 2)
  const [allocateBatch, setAllocateBatch] = useState(null);

  // Recipe access gate — password-protected session token
  const hasRecipeAccess = isAdmin || user?.permissions?.recipes;
  const [recipeToken, setRecipeToken] = useState(() => sessionStorage.getItem('ti_recipe_token') || '');
  const [pkgScan, setPkgScan] = useState(null);
  const [pkgPicks, setPkgPicks] = useState({});
  const [pkgBusy, setPkgBusy] = useState(false);
  const [showRecipeAuth, setShowRecipeAuth] = useState(false);
  const [recipePwd, setRecipePwd] = useState('');
  const [recipeAuthErr, setRecipeAuthErr] = useState('');
  const [recipeAuthLoading, setRecipeAuthLoading] = useState(false);

  // Batch form
  const [batchForm, setBatchForm] = useState({
    product_id: '', num_batches: '1', production_line_id: '', notes: '', for_client: 'General Stock', for_client_id: '',
    production_date: new Date().toISOString().slice(0, 10),  // iter185 — defaults to today, user can backdate
  });
  const [feasibility, setFeasibility] = useState(null);
  // "Materials not in stock" popup: amber (unlotted) + red (short) rows from the server pre-check
  const [stockConfirm, setStockConfirm] = useState(null);

  // Recipe form
  const [recipeForm, setRecipeForm] = useState({
    product_name: '', product_id: '', product_code: '', batch_size_kg: '', notes: '',
    materials: [{ material_id: '', material_name: '', quantity_per_batch: '', unit: 'kg', phase: 'premix' }]
  });

  // Define fetchData before useAdminDelete to avoid TDZ error
  const fetchData = async () => {
    try {
      const recipeHeaders = recipeToken ? { 'X-Recipe-Token': recipeToken } : {};
      const [bRes, rRes, pRes, mRes, lRes, tRes, cRes] = await Promise.all([
        axios.get(`${API}/production/batches`, { withCredentials: true }),
        // Only fetch recipes if user has access AND has unlocked with password
        hasRecipeAccess && recipeToken
          ? axios.get(`${API}/production/recipes`, { withCredentials: true, headers: recipeHeaders }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        axios.get(`${API}/inventory/finished-goods`, { withCredentials: true }),
        axios.get(`${API}/inventory/raw-materials`, { withCredentials: true }),
        axios.get(`${API}/production/lines`, { withCredentials: true }),
        axios.get(`${API}/production/timeline`, { withCredentials: true }),
        axios.get(`${API}/sales/orders`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setBatches(bRes.data); setRecipes(rRes.data); setProducts(pRes.data);
      setMaterials(mRes.data); setProdLines(lRes.data); setTimeline(tRes.data);
      const uniqueClients = [...new Set((cRes.data || []).map(o => o.client).filter(Boolean))];
      setClients(uniqueClients);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete } = useAdminDelete(fetchData);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/production/history`, { withCredentials: true });
      setHistoryBatches(res.data);
    } catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  };

  useEffect(() => { if (tab === 'history') fetchHistory(); }, [tab]);

  useEffect(() => { fetchData(); }, [recipeToken]);

  // iter238 — packaging re-tagging helper. Packaging lines mixed into the
  // ingredients inflate batch size and print on the operator's sheet; this
  // finds them and moves them only after the owner approves the list.
  const scanPackaging = async () => {
    try {
      const r = await axios.get(`${API}/production/recipe-packaging/scan`, {
        withCredentials: true, headers: { 'X-Recipe-Token': recipeToken || '' },
      });
      setPkgScan(r.data);
      setPkgPicks(Object.fromEntries((r.data.hits || []).map((h, i) => [i, true])));
      if (!r.data.count) toast.success('No packaging lines found in the ingredients — recipes are clean');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not scan recipes');
    }
  };

  const applyPackaging = async () => {
    const lines = (pkgScan?.hits || []).filter((_, i) => pkgPicks[i]);
    if (!lines.length) { toast.error('Select at least one line'); return; }
    setPkgBusy(true);
    try {
      const r = await axios.post(`${API}/production/recipe-packaging/apply`, { lines },
        { withCredentials: true, headers: { 'X-Recipe-Token': recipeToken || '' } });
      toast.success(`${r.data.recipes_changed} recipe(s) updated — batch sizes recalculated`);
      setPkgScan(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update recipes');
    } finally { setPkgBusy(false); }
  };

  // Recipe password verification
  const verifyRecipePassword = async () => {
    setRecipeAuthLoading(true); setRecipeAuthErr('');
    try {
      const res = await axios.post(`${API}/production/recipes/verify-password`, { password: recipePwd }, { withCredentials: true });
      const tok = res.data.token || recipePwd;
      sessionStorage.setItem('ti_recipe_token', tok);
      setRecipeToken(tok);
      setShowRecipeAuth(false);
      setRecipePwd('');
      toast.success('Recipes unlocked for this session', { duration: 1800, position: 'bottom-right' });
    } catch (e) {
      setRecipeAuthErr(e.response?.data?.detail || 'Incorrect password');
    } finally { setRecipeAuthLoading(false); }
  };

  const lockRecipes = () => {
    sessionStorage.removeItem('ti_recipe_token');
    setRecipeToken('');
    toast.info('Recipes locked');
  };

  // Prompt for password when user opens Recipes tab without a token
  useEffect(() => {
    if (tab === 'recipes' && hasRecipeAccess && !recipeToken && !showRecipeAuth) {
      setShowRecipeAuth(true);
    }
  }, [tab, hasRecipeAccess, recipeToken, showRecipeAuth]);

  // Feasibility check
  useEffect(() => {
    setFeasibility(null);
    if (batchForm.product_id && batchForm.num_batches && parseInt(batchForm.num_batches) > 0) {
      const timer = setTimeout(async () => {
        try {
          const res = await axios.get(
            `${API}/production/recipe-check/${batchForm.product_id}?quantity=${batchForm.num_batches}`,
            { withCredentials: true }
          );
          setFeasibility(res.data);
        } catch (e) { console.error(e); }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [batchForm.product_id, batchForm.num_batches]);

  const updateBatchStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/production/batches/${id}/status`, { status }, { withCredentials: true });
      setBatches(prev => prev.map(b => b.id === id ? res.data : b));
      toast.success(`Batch ${id} → ${status}`);
      if (status === 'In Production') fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const updateBatchDetails = async (id, details) => {
    try {
      const res = await axios.patch(`${API}/production/batches/${id}/details`, details, { withCredentials: true });
      setBatches(prev => prev.map(b => b.id === id ? res.data : b));
      toast.success('Updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const getLineColor = (lineId) => {
    const line = prodLines.find(l => l.id === lineId);
    return line?.color || '#6b7280';
  };

  // Stage 0 — case-insensitive, word-based search over batch id, product name and client.
  const matchesBatchSearch = (b, q) => {
    const words = (q || '').toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return true;
    const hay = `${b.id || ''} ${b.product_name || ''} ${b.for_client || ''} ${b.product_code || ''}`.toLowerCase();
    return words.every(w => hay.includes(w));
  };
  const searchMatches = useMemo(() => {
    if (!batchSearch.trim()) return [];
    const seen = new Set();
    const pool = [...batches, ...historyBatches, ...timeline.flatMap(d => d.batches || [])];
    const out = [];
    for (const b of pool) {
      if (!b?.id || seen.has(b.id) || !matchesBatchSearch(b, batchSearch)) continue;
      seen.add(b.id); out.push(b);
    }
    const q = batchSearch.trim().toLowerCase();
    out.sort((a, b) => ((a.id || '').toLowerCase() === q ? 0 : 1) - ((b.id || '').toLowerCase() === q ? 0 : 1));
    return out;
  }, [batchSearch, batches, historyBatches, timeline]);

  const filteredBatches = batches.filter(b => {
    if (lineFilter !== 'All' && b.production_line_id !== lineFilter) return false;
    if (statusFilter !== 'All' && b.status !== statusFilter) return false;
    if (clientFilter && !b.for_client?.toLowerCase().includes(clientFilter.toLowerCase())) return false;
    if (productFilter && !b.product_name?.toLowerCase().includes(productFilter.toLowerCase())) return false;
    if (batchSearch && !matchesBatchSearch(b, batchSearch)) return false;
    return true;
  });

  const filteredHistory = historyBatches.filter(b => {
    if (lineFilter !== 'All' && b.production_line_id !== lineFilter) return false;
    if (clientFilter && !b.for_client?.toLowerCase().includes(clientFilter.toLowerCase())) return false;
    if (productFilter && !b.product_name?.toLowerCase().includes(productFilter.toLowerCase())) return false;
    if (batchSearch && !matchesBatchSearch(b, batchSearch)) return false;
    return true;
  });

  const filteredTimeline = timeline.map(d => ({
    ...d,
    batches: d.batches.filter(b => {
      if (lineFilter !== 'All' && b.production_line_id !== lineFilter) return false;
      if (statusFilter !== 'All' && b.status !== statusFilter) return false;
      if (clientFilter && !b.for_client?.toLowerCase().includes(clientFilter.toLowerCase())) return false;
      if (productFilter && !b.product_name?.toLowerCase().includes(productFilter.toLowerCase())) return false;
      if (batchSearch && !matchesBatchSearch(b, batchSearch)) return false;
      return true;
    }),
  })).filter(d => d.batches.length > 0);

  const handleCreateBatch = async (e, opts = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    setSubmitting(true);
    try {
      const product = products.find(p => p.id === batchForm.product_id);
      const payload = {
        ...batchForm,
        product_name: product?.name || '',
        num_batches: parseInt(batchForm.num_batches),
        batch_id_override: batchForm.batch_id_override || undefined,
      };
      if (opts.allowShortage) payload.allow_shortage = true;
      if (opts.allowUnlotted) payload.allow_unlotted = true;
      const res = await axios.post(`${API}/production/batches`, payload, { withCredentials: true });
      // iter185 — server returns single object for n=1, or {count, batches[]} for n>1
      const newBatches = Array.isArray(res.data?.batches) ? res.data.batches : [res.data];
      setBatches(prev => [...newBatches, ...prev]);
      setShowBatchModal(false);
      setBatchForm({ product_id: '', num_batches: '1', production_line_id: '', notes: '', batch_id_override: '', production_date: new Date().toISOString().slice(0, 10) });
      setFeasibility(null);
      setStockConfirm(null);
      const unl = res.data?.unlotted_info?.length || 0;
      if (res.data?.rm_shortage_info?.length) {
        toast.warning(`${newBatches.length} batch(es) created WITH stock debt — ${res.data.rm_shortage_info.length} RM shortage(s) pending next inward${unl ? `, ${unl} lot assignment(s) pending` : ''}.`);
      } else if (unl) {
        toast.warning(`${newBatches.length === 1 ? `Batch ${newBatches[0].id}` : `${newBatches.length} batches`} created — ${unl} material(s) recorded as UNLOTTED. Assign the lot once the purchase is entered.`);
      } else {
        toast.success(newBatches.length === 1
          ? `Batch ${newBatches[0].id} created!`
          : `${newBatches.length} batches created: ${newBatches.map(b => b.id).join(', ')}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Pre-check says lots/total are missing → the "materials not in stock" popup (never a plain toast)
      if (err.response?.status === 400 && detail && typeof detail === 'object' &&
          (Array.isArray(detail.shortages) || Array.isArray(detail.unlotted))) {
        setStockConfirm({ shortages: detail.shortages || [], unlotted: detail.unlotted || [] });
      } else {
        const rid = err.response?.data?.request_id;
        const msg = typeof detail === 'string' ? detail : (detail?.error || 'Failed to create batch');
        toast.error(rid ? `${msg} · ref ${rid}` : msg);
      }
    }
    finally { setSubmitting(false); }
  };

  const handleSaveRecipe = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // batch_size_kg = sum of materials quantity_per_batch (auto-computed). The
      // backend re-asserts the same rule, so we just send 0 to let it derive.
      // iter238 — batch size = ingredients only; packaging carries no weight.
      const computedBatch = recipeForm.materials.reduce((s, m) => s + weightOf(m), 0);
      const payload = {
        ...recipeForm,
        batch_size_kg: computedBatch,
        materials: recipeForm.materials.filter(m => m.material_id && m.quantity_per_batch),
      };
      const recipeHeaders = { 'X-Recipe-Token': recipeToken || '' };
      if (editRecipe) {
        await axios.put(`${API}/production/recipes/${editRecipe.id}`, payload, { withCredentials: true, headers: recipeHeaders });
        toast.success('Recipe updated!');
      } else {
        await axios.post(`${API}/production/recipes`, payload, { withCredentials: true, headers: recipeHeaders });
        toast.success('Recipe created!');
      }
      setShowRecipeModal(false);
      setEditRecipe(null);
      setRecipeForm({ product_name: '', product_id: '', product_code: '', batch_size_kg: '', notes: '', materials: [{ material_id: '', material_name: '', quantity_per_batch: '', unit: 'kg' }] });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  // deleteRecipe now uses useAdminDelete hook via requestDelete

  const openEditRecipe = (r) => {
    setEditRecipe(r);
    setRecipeForm({
      product_name: r.product_name,
      product_id: r.product_id,
      product_code: r.product_code || '',
      batch_size_kg: String(r.batch_size_kg || ''),
      notes: r.notes || '',
      materials: r.materials?.length > 0
        ? r.materials.map(m => ({
            material_id: m.material_id,
            material_name: m.material_name,
            quantity_per_batch: String(m.quantity_per_batch || m.quantity_required || ''),
            unit: m.unit || 'kg',
            phase: m.phase || 'premix',
          }))
        : [{ material_id: '', material_name: '', quantity_per_batch: '', unit: 'kg', phase: 'premix' }],
    });
    setShowRecipeModal(true);
  };

  const addIngredientRow = () => {
    setRecipeForm(f => ({
      ...f,
      materials: [...f.materials, { material_id: '', material_name: '', quantity_per_batch: '', unit: 'kg' }]
    }));
  };

  const removeIngredientRow = (idx) => {
    setRecipeForm(f => ({
      ...f,
      materials: f.materials.filter((_, i) => i !== idx),
    }));
  };

  const updateIngredient = (idx, field, value) => {
    setRecipeForm(f => {
      const mats = [...f.materials];
      mats[idx] = { ...mats[idx], [field]: value };
      if (field === 'material_id') {
        const mat = materials.find(m => m.id === value);
        if (mat) mats[idx].material_name = mat.name;
      }
      return { ...f, materials: mats };
    });
  };

  const loadBatchDetail = async (batchId) => {
    try {
      const res = await axios.get(`${API}/production/batches/${batchId}`, { withCredentials: true });
      setBatchDetail(res.data);
      setExpandedBatch(batchId);
    } catch (e) { toast.error('Failed to load batch detail'); }
  };

  // Maintenance alerts
  const maintenanceAlerts = useMemo(() => {
    return prodLines.filter(line => {
      if (!line.last_maintenance) return true;
      const last = new Date(line.last_maintenance);
      const interval = line.maintenance_interval_days || 30;
      const due = new Date(last.getTime() + interval * 86400000);
      return due <= new Date();
    });
  }, [prodLines]);

  const recipesForProduct = useMemo(() => {
    const map = {};
    recipes.forEach(r => { map[r.product_id] = r; });
    return map;
  }, [recipes]);

  const completedCount = batches.filter(b => b.status === 'Completed').length;
  const activeCount = batches.filter(b => b.status === 'In Production').length;

  if (loading) return <div className="text-zinc-400 animate-pulse text-sm">Loading production...</div>;

  return (
    <div className="space-y-4" data-testid="production-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Production Management</h1>
          <p className="text-zinc-500 text-sm mt-0.5">{batches.length} batches / {activeCount} active / {completedCount} completed / {recipes.length} recipes</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} title="Refresh batches from DB"
            data-testid="production-refresh-btn"
            className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs px-3 py-2 rounded-xl border border-zinc-700">
            <RefreshCw size={13} /> <span className="hidden sm:inline">Refresh</span>
          </button>
          {hasRecipeAccess && recipeToken && (
            <button onClick={() => { setEditRecipe(null); setRecipeForm({ product_name: '', product_id: '', product_code: '', batch_size_kg: '', notes: '', materials: [{ material_id: '', material_name: '', quantity_per_batch: '', unit: 'kg' }] }); setShowRecipeModal(true); }}
              className="flex items-center gap-1.5 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 font-semibold text-xs px-3 py-2 rounded-xl" data-testid="add-recipe-btn">
              <FlaskConical size={13} /> Add Recipe
            </button>
          )}
          {hasRecipeAccess && recipeToken && (
            <button onClick={scanPackaging} data-testid="scan-packaging-btn"
              title="Find recipe lines that look like packaging but are still counted as product weight"
              className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-amber-300 text-xs px-3 py-2 rounded-xl border border-amber-500/30">
              <Package size={13} /> Find packaging lines
            </button>
          )}
          <button data-testid="create-batch-btn" onClick={() => setShowBatchModal(true)}
            className="flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white font-semibold text-xs px-3 py-2 rounded-xl">
            <Plus size={14} /> Create Batch
          </button>
        </div>
      </div>

      {/* Maintenance Alerts */}
      {maintenanceAlerts.length > 0 && (
        <div className="bg-orange-950/30 border border-orange-700/50 rounded-xl p-3 flex items-start gap-3">
          <Wrench size={16} className="text-orange-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-orange-300 text-xs font-semibold">Maintenance Due</p>
            <p className="text-orange-400/80 text-xs mt-0.5">
              {maintenanceAlerts.map(l => l.name).join(', ')} — Please clean, inspect, and record maintenance.
            </p>
          </div>
        </div>
      )}

      {/* Status Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
        {BATCH_STATUSES.map(s => (
          <div key={s} className={`p-3 rounded-xl border text-center ${BATCH_STYLES[s]}`}>
            <div className="text-xl font-heading font-bold">{batches.filter(b => b.status === s).length}</div>
            <div className="text-xs mt-0.5 opacity-80">{s}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 overflow-x-auto max-w-full">
        {[
          ['kanban', 'Kanban', LayoutGrid],
          ['batches', 'Batches', Layers],
          ['timeline', 'Production Log', Calendar],
          ['history', 'History', Clock],
          ...(hasRecipeAccess ? [['recipes', 'Recipes', FlaskConical]] : []),
          ['lines', 'Lines & Maintenance', Wrench],
        ].map(([key, label, Icon]) => (
          <button key={key} data-testid={`prod-tab-${key}`} onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${tab === key ? 'bg-red-600 text-white font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}>
            <Icon size={13} />{label}
          </button>
        ))}
      </div>

      {/* Filter bar (for kanban, batches, timeline, history) */}
      {['kanban', 'batches', 'timeline', 'history'].includes(tab) && (
        <div className="flex flex-wrap gap-2 items-center">
          <select value={lineFilter} onChange={e => setLineFilter(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 rounded-lg px-2.5 py-1.5 text-zinc-400 text-xs focus:outline-none" data-testid="line-filter">
            <option value="All">All Lines</option>
            {prodLines.map(l => <option key={l.id} value={l.id} style={{color: l.color}}>{l.name}</option>)}
          </select>
          {tab !== 'kanban' && tab !== 'history' && (
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded-lg px-2.5 py-1.5 text-zinc-400 text-xs focus:outline-none" data-testid="status-filter">
              <option value="All">All Status</option>
              {BATCH_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          <input value={productFilter} onChange={e => setProductFilter(e.target.value)} placeholder="Product..."
            data-testid="product-filter"
            className="bg-zinc-900 border border-zinc-800 rounded-lg px-2.5 py-1.5 text-zinc-300 text-xs focus:outline-none w-28" />
          <input value={clientFilter} onChange={e => setClientFilter(e.target.value)} placeholder="Client..."
            data-testid="client-filter"
            className="bg-zinc-900 border border-zinc-800 rounded-lg px-2.5 py-1.5 text-zinc-300 text-xs focus:outline-none w-28" />
          {(tab === 'timeline' || tab === 'history' || tab === 'batches') && (
            <div className="relative">
              <input value={batchSearch}
                onChange={e => { setBatchSearch(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                onKeyDown={e => { if (e.key === 'Enter' && searchMatches.length === 1) { setOpenBatch(searchMatches[0]); setSearchOpen(false); } if (e.key === 'Escape') setSearchOpen(false); }}
                placeholder="Batch ID or product..."
                data-testid="batch-search"
                className="bg-zinc-900 border border-zinc-800 rounded-lg px-2.5 py-1.5 text-zinc-300 text-xs focus:outline-none w-44" />
              {searchOpen && batchSearch.trim() && (
                <div data-testid="batch-search-results" className="absolute left-0 top-full mt-1 z-40 w-80 max-h-72 overflow-auto rounded-lg border border-zinc-700 bg-zinc-950 shadow-xl">
                  {searchMatches.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-zinc-500" data-testid="batch-search-empty">No batch matches "{batchSearch.trim()}"</div>
                  ) : (
                    <>
                      <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                        {searchMatches.length} match{searchMatches.length === 1 ? '' : 'es'}{searchMatches.length > 1 ? ' — pick one' : ''}
                      </div>
                      {searchMatches.slice(0, 12).map(b => (
                        <button key={b.id} type="button" data-testid={`batch-search-result-${b.id}`}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => { setOpenBatch(b); setSearchOpen(false); }}
                          className="w-full text-left px-3 py-2 hover:bg-zinc-800/80 border-b border-zinc-900 last:border-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono text-xs text-amber-300">{b.batch_label || b.id}</span>
                            <span className="text-[10px] text-zinc-500">{b.status || ''}</span>
                          </div>
                          <div className="text-xs text-zinc-200 truncate">{b.product_name || '—'}{b.for_client ? <span className="text-zinc-500"> · {b.for_client}</span> : null}</div>
                          <div className="text-[10px] text-zinc-500 font-mono">{b.production_date || (b.created_at || '').slice(0, 10)}</div>
                        </button>
                      ))}
                      {searchMatches.length > 12 && <div className="px-3 py-1.5 text-[10px] text-zinc-500">Type more words to narrow {searchMatches.length} matches</div>}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          <span className="text-zinc-600 text-[10px] font-mono">{filteredBatches.length} batches</span>
        </div>
      )}

      {/* ══ KANBAN TAB ══ */}
      {tab === 'kanban' && (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="production-kanban">
          {BATCH_STATUSES.map(status => {
            const colBatches = filteredBatches.filter(b => b.status === status);
            const colStyle = {
              'Planned': 'border-zinc-700 bg-zinc-950',
              'In Production': 'border-amber-800 bg-amber-950/10',
              'QC': 'border-blue-800 bg-blue-950/10',
              'Packing': 'border-purple-800 bg-purple-950/10',
              'Completed': 'border-green-800 bg-green-950/10',
            }[status];
            const nextStatus = { 'Planned': 'In Production', 'In Production': 'QC', 'QC': 'Packing', 'Packing': 'Completed' }[status];
            return (
              <div key={status} className={`flex-shrink-0 w-72 rounded-xl border ${colStyle} flex flex-col`} data-testid={`kanban-col-${status.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="px-3 py-2.5 border-b border-zinc-800/60 flex items-center justify-between">
                  <span className={`text-xs font-semibold ${BATCH_STYLES[status]?.split(' ').find(c => c.startsWith('text-'))}`}>{status}</span>
                  <span className="text-xs bg-zinc-700 text-zinc-300 px-1.5 py-0.5 rounded-full font-mono">{colBatches.length}</span>
                </div>
                <div className="p-2 space-y-2 flex-1 min-h-24 max-h-[60vh] overflow-y-auto">
                  {colBatches.map(b => (
                    <div key={b.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 hover:border-zinc-700 transition-all" data-testid={`kanban-batch-${b.id}`}>
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <div className="font-mono text-amber-400 text-xs font-bold">{b.batch_label || b.id}{b.batch_label && b.batch_label !== b.id ? <span className="text-zinc-500 font-normal ml-1">({b.id})</span> : null}</div>
                          <div className="text-zinc-200 text-xs font-medium mt-0.5 flex items-center gap-1.5 flex-wrap">
                            <ProductCodeBadge name={b.product_name} code={b.product_code} />
                            <CodeLabel name={b.product_name} codeClass="font-mono text-sky-300 text-[10px] font-semibold" />
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <SubscribeButton refType="batch" refId={b.id} label={`${b.id} · ${b.product_name}`} size="xs" />
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-mono font-bold text-white" style={{ backgroundColor: getLineColor(b.production_line_id) }}>
                            {b.production_line_name || b.production_line_code || '—'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-zinc-500 mt-1">
                        <span>{b.num_batches || 1}x{b.batch_size_kg}kg</span>
                        <span>= <span className="text-zinc-300 font-mono">{b.total_output_kg}kg</span></span>
                        {b.for_client && b.for_client !== 'General Stock' && <span className="text-blue-400">| {b.for_client}</span>}
                      </div>
                      {/* Batch-weighing linkage indicator (Planned column) */}
                      {b.status === 'Planned' && b.weighed && (
                        <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-emerald-500/60 bg-emerald-500/15 text-emerald-200 text-[10px] font-semibold" data-testid={`weighed-badge-${b.id}`}>
                          <CheckCircle2 size={9} /> Ingredients weighed
                          {b.weighed_by && <span className="opacity-70">· {b.weighed_by}</span>}
                        </div>
                      )}
                      {/* Wastage/Output for any batch with actual data (iter188 — was Completed-only) */}
                      {b.actual_output_kg > 0 && (
                        <div className="text-[10px] mt-1 flex gap-2 flex-wrap">
                          <span className="text-green-400">Output: {b.actual_output_kg}kg</span>
                          {b.wastage_kg > 0 && <span className="text-red-400">Waste: {b.wastage_kg}kg ({b.wastage_pct || 0}%)</span>}
                        </div>
                      )}
                      {/* Remark */}
                      {b.remark && <div className="text-[9px] text-zinc-600 mt-1 italic truncate">{b.remark}</div>}
                      {/* Material info for In Production */}
                      {b.status === 'In Production' && b.materials_consumed?.length > 0 && (
                        <div className="text-[9px] text-amber-400/70 mt-1">{b.materials_consumed.length} lots consumed</div>
                      )}
                      {/* Actions */}
                      <div className="flex items-center gap-1 mt-2">
                        {status !== 'Planned' && (
                          <button
                            onClick={() => {
                              const prev = { 'In Production': 'Planned', 'QC': 'In Production', 'Packing': 'QC', 'Completed': 'Packing' }[status];
                              if (prev) updateBatchStatus(b.id, prev);
                            }}
                            title="Move back one column"
                            data-testid={`kanban-back-${b.id}`}
                            className="p-1 rounded-lg bg-zinc-800 text-zinc-400 hover:text-amber-400 hover:bg-zinc-700">
                            <ArrowLeft size={11} />
                          </button>
                        )}
                        {nextStatus && (
                          <button onClick={() => updateBatchStatus(b.id, nextStatus)}
                            data-testid={`kanban-forward-${b.id}`}
                            className="flex-1 text-[10px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg py-1 flex items-center justify-center gap-1 transition-colors">
                            <ArrowRight size={9} /> {nextStatus}
                          </button>
                        )}
                        <button onClick={() => setEditTimings(b)} title="Timings / Details"
                          className="p-1 rounded-lg bg-zinc-800 text-zinc-400 hover:text-amber-400">
                          <Clock size={11} />
                        </button>
                        {b.fifo_allocated && (
                          <button onClick={() => setBatchSheetBatchId(b.id)} title="Print weighing sheet (FIFO lots)"
                            data-testid={`kanban-print-sheet-${b.id}`}
                            className="p-1 rounded-lg bg-amber-500/20 text-amber-300 hover:text-amber-200 border border-amber-500/40">
                            <Printer size={11} />
                          </button>
                        )}
                        {isAdmin && <button onClick={() => requestDelete(`/production/batches/${b.id}`, b.id, `Delete batch ${b.id}. FIFO restored, FG reversed.`)}
                          className="p-1 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30"><Trash2 size={10} /></button>}
                      </div>
                    </div>
                  ))}
                  {colBatches.length === 0 && <div className="text-zinc-700 text-[10px] text-center py-4">No batches</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══ BATCHES TAB ══ */}
      {tab === 'batches' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead><tr className="border-b border-zinc-800">
              {['Batch ID', 'Product', 'Batches', 'Output (kg)', ...(canSeeCost ? ['Cost (Rs.)'] : []), 'Line', 'For', 'Status', 'FIFO Lots', 'Date', 'Actions'].map(h => (
                <th key={h} className="text-left text-zinc-500 text-xs font-medium px-3 py-3">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {batches.map(b => (
                <React.Fragment key={b.id}>
                  <tr className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer" data-testid={`batch-row-${b.id}`}
                    onClick={() => expandedBatch === b.id ? setExpandedBatch(null) : loadBatchDetail(b.id)}>
                    <td className="px-3 py-3 font-mono text-amber-400 text-xs font-bold">{b.id}</td>
                    <td className="px-3 py-3 text-zinc-200 text-xs font-medium">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <ProductCodeBadge name={b.product_name} code={b.product_code} />
                        <CodeLabel name={b.product_name} />
                      </div>
                    </td>
                    <td className="px-3 py-3 font-mono text-zinc-300 text-xs">{b.num_batches || 1}</td>
                    <td className="px-3 py-3 font-mono text-xs">
                      {/* iter188 — show actual output if set, else planned. Loss% shown beneath when wastage > 0. */}
                      {b.actual_output_kg > 0 ? (
                        <div>
                          <div className="text-emerald-300">{b.actual_output_kg} kg</div>
                          {b.wastage_kg > 0 && (
                            <div className="text-[9px] text-red-400">−{b.wastage_kg}kg ({b.wastage_pct || 0}%)</div>
                          )}
                          <div className="text-[9px] text-zinc-600">plan {b.total_output_kg || b.quantity} kg</div>
                        </div>
                      ) : (
                        <span className="text-zinc-300">{b.total_output_kg || b.quantity} kg</span>
                      )}
                    </td>
                    {canSeeCost && (
                    <td className="px-3 py-3 font-mono text-xs" data-testid={`batch-cost-${b.id}`}>
                      {/* iter215 — FIFO actual COGS. rm_cost_actual is in INR.
                          rm_cost_per_kg recomputes on yield update.
                          iter217 — visible to admin/owner only. Now also
                          shows overhead (Phase 2) if allocated, plus total. */}
                      {b.rm_cost_actual > 0 ? (
                        <div>
                          <div className="text-amber-300">Rs.{Number(b.rm_cost_actual).toLocaleString('en-IN')}</div>
                          <div className="text-[9px] text-zinc-500">Rs.{b.rm_cost_per_kg || 0}/kg</div>
                          {b.overhead_cost_actual > 0 && (
                            <>
                              <div className="text-[9px] text-emerald-400 mt-0.5">+ovh Rs.{Number(b.overhead_cost_actual).toLocaleString('en-IN')}</div>
                              <div className="text-[9px] text-emerald-300 font-bold border-t border-zinc-800 pt-0.5 mt-0.5">
                                = Rs.{Number(b.total_cost_actual || 0).toLocaleString('en-IN')}
                              </div>
                              <div className="text-[8px] text-zinc-500">Rs.{b.cost_per_kg_with_overhead || 0}/kg full</div>
                            </>
                          )}
                          <button
                            onClick={e => { e.stopPropagation(); setAllocateBatch(b); }}
                            data-testid={`allocate-overhead-${b.id}`}
                            title={b.overhead_cost_actual > 0 ? 'Re-allocate overhead' : 'Allocate overhead (Phase 2)'}
                            className="mt-1 text-[8px] uppercase tracking-wide bg-emerald-900/30 hover:bg-emerald-900/50 text-emerald-300 border border-emerald-700/40 rounded px-1.5 py-0.5"
                          >
                            {b.overhead_cost_actual > 0 ? '↻ ovh' : '+ ovh'}
                          </button>
                        </div>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                    )}
                    <td className="px-3 py-3 text-xs">
                      {b.production_line_name && <span className="bg-amber-900/30 text-amber-400 border border-amber-700/40 px-1.5 py-0.5 rounded-md font-mono text-[10px]">{b.production_line_name}</span>}
                    </td>
                    <td className="px-3 py-3 text-xs text-zinc-300">{b.for_client || 'General Stock'}</td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${BATCH_STYLES[b.status]}`}>{b.status}</span>
                    </td>
                    <td className="px-3 py-3">
                      {b.materials_consumed?.length > 0 ? (
                        <span className="text-green-400 text-xs">{b.materials_consumed.length} lots</span>
                      ) : (
                        <span className="text-zinc-600 text-xs">{b.fifo_allocated ? 'Done' : 'Pending'}</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-zinc-500 text-xs font-mono">{b.production_date || b.created_at?.slice(0, 10)}</td>
                    <td className="px-3 py-3">
                      {b.status !== 'Completed' ? (
                        <select value={b.status} onChange={e => { e.stopPropagation(); updateBatchStatus(b.id, e.target.value); }}
                          onClick={e => e.stopPropagation()}
                          data-testid={`batch-status-${b.id}`}
                          className={`text-xs px-2 py-1 rounded-lg border bg-transparent cursor-pointer ${BATCH_STYLES[b.status]}`}>
                          {BATCH_STATUSES.map(s => <option key={s} value={s} className="bg-zinc-900 text-zinc-50">{s}</option>)}
                        </select>
                      ) : (
                        <span className="text-green-400 text-xs flex items-center gap-1"><CheckCircle2 size={12} /> Done</span>
                      )}
                      {/* iter204 — Print Batch Sheet button. Available for any
                          batch whose RM lots are reserved (planning onwards). */}
                      {b.fifo_allocated && (
                        <button
                          onClick={e => { e.stopPropagation(); setBatchSheetBatchId(b.id); }}
                          title="Print weighing sheet (FIFO lot allocation)"
                          data-testid={`print-batch-sheet-${b.id}`}
                          className="p-1 rounded-lg bg-amber-500/20 text-amber-300 hover:text-amber-200 border border-amber-500/40 ml-1.5">
                          <Printer size={12} />
                        </button>
                      )}
                      {isAdmin && <button onClick={e => { e.stopPropagation(); requestDelete(`/production/batches/${b.id}`, b.id, `Delete batch ${b.id}. FIFO materials restored, FG stock reversed.`); }} className="p-1 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30 ml-1.5"><Trash2 size={12} /></button>}
                    </td>
                  </tr>
                  {/* Drill-down: materials consumed with lot traceability */}
                  {expandedBatch === b.id && batchDetail && (
                    <tr><td colSpan={canSeeCost ? 11 : 10} className="p-0">
                      <div className="bg-zinc-950/60 border-b border-zinc-800 px-4 py-3">
                        <p className="text-zinc-400 text-xs font-semibold mb-2 flex items-center gap-2">
                          <Package size={12} className="text-amber-400" />
                          Materials Consumed — Batch {b.id} ({b.num_batches || 1} batch(es) x {b.batch_size_kg || '?'} kg)
                        </p>
                        {batchDetail.materials_consumed?.length > 0 ? (
                          <table className="w-full">
                            <thead><tr className="border-b border-zinc-800/60">
                              {['Material', 'Lot No', 'Qty Used (kg)', 'Rate/kg', 'Cost', 'Vendor', 'Lot Date'].map(h => (
                                <th key={h} className="text-left text-zinc-500 text-[10px] font-medium px-2 py-1.5">{h}</th>
                              ))}
                            </tr></thead>
                            <tbody>
                              {batchDetail.materials_consumed.map((mc, i) => (
                                <tr key={i} className="border-b border-zinc-800/30 hover:bg-zinc-800/20">
                                  <td className="px-2 py-1.5 text-zinc-300 text-[11px]"><ItemCodeLabel name={mc.material_name} /></td>
                                  <td className="px-2 py-1.5">
                                    <span className="font-mono text-[10px] bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1.5 py-0.5 rounded-md">{mc.lot_no || '-'}</span>
                                  </td>
                                  <td className="px-2 py-1.5 font-mono text-zinc-200 text-[11px]">{mc.quantity_used?.toFixed(2)}</td>
                                  <td className="px-2 py-1.5 font-mono text-zinc-400 text-[11px]">{mc.cost_per_unit > 0 ? fmt(mc.cost_per_unit) : '-'}</td>
                                  <td className="px-2 py-1.5 font-mono text-amber-400 text-[11px]">{mc.cost_per_unit > 0 ? fmt(mc.quantity_used * mc.cost_per_unit) : '-'}</td>
                                  <td className="px-2 py-1.5 text-zinc-500 text-[11px]">{mc.lot_vendor || '-'}</td>
                                  <td className="px-2 py-1.5 text-zinc-500 text-[11px] font-mono">{mc.lot_purchase_date?.slice(0, 10) || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="text-zinc-600 text-xs">No materials consumed yet. Move to "In Production" to trigger FIFO allocation.</p>
                        )}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {batches.length === 0 && <div className="text-center text-zinc-500 text-sm py-8">No batches yet</div>}
        </div>
      )}

      {/* ══ PRODUCTION LOG / TIMELINE TAB ══ */}
      {tab === 'timeline' && (
        <div className="space-y-3">
          {/* Month picker */}
          <div className="flex items-center gap-3">
            <input type="month" value={calMonth} onChange={e => setCalMonth(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2 text-zinc-50 text-sm font-mono focus:outline-none focus:border-red-500" />
            <span className="text-zinc-500 text-xs">
              {filteredTimeline.filter(d => d.date.startsWith(calMonth)).reduce((s, d) => s + d.batches.length, 0)} batches in {calMonth}
            </span>
          </div>
          {/* Calendar grid */}
          {(() => {
            const [y, mo] = calMonth.split('-').map(Number);
            const firstDay = new Date(y, mo - 1, 1).getDay();
            const daysInMonth = new Date(y, mo, 0).getDate();
            const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
            const batchMap = {};
            filteredTimeline.forEach(d => { batchMap[d.date] = d.batches; });
            const cells = [];
            for (let i = 0; i < firstDay; i++) cells.push(null);
            for (let d = 1; d <= daysInMonth; d++) cells.push(d);
            const todayStr = new Date().toISOString().slice(0, 10);
            return (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                <div className="grid grid-cols-7">
                  {dayNames.map(d => <div key={d} className="text-center text-zinc-500 text-xs font-medium py-2 border-b border-zinc-800">{d}</div>)}
                  {cells.map((day, i) => {
                    if (!day) return <div key={`e-${i}`} className="min-h-[60px] border-b border-r border-zinc-800/40" />;
                    const dateStr = `${y}-${String(mo).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                    const dayBatches = batchMap[dateStr] || [];
                    const isToday = dateStr === todayStr;
                    const isSel = expandedDate === dateStr;
                    return (
                      <div key={day} onClick={() => setExpandedDate(isSel ? null : dateStr)}
                        className={`min-h-[60px] border-b border-r border-zinc-800/40 px-1.5 py-1 cursor-pointer transition-colors ${isSel ? 'bg-red-900/20' : isToday ? 'bg-amber-900/10' : 'hover:bg-zinc-800/30'}`}>
                        <div className={`text-xs font-mono ${isToday ? 'text-amber-400 font-bold' : 'text-zinc-500'}`}>{day}</div>
                        {dayBatches.length > 0 && (
                          <div className="mt-0.5 space-y-0.5">
                            {dayBatches.slice(0, 2).map((b, j) => (
                              <div key={j} className="text-[8px] bg-red-900/40 text-red-300 border border-red-800/40 rounded px-1 py-0 truncate">{b.id}</div>
                            ))}
                            {dayBatches.length > 2 && <div className="text-[8px] text-zinc-600">+{dayBatches.length - 2}</div>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {/* Selected date detail */}
          {expandedDate && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center gap-3">
                <Calendar size={14} className="text-red-400" />
                <span className="text-zinc-100 text-sm font-semibold font-mono">{expandedDate}</span>
              </div>
              {(() => {
                const dayBatches = filteredTimeline.find(d => d.date === expandedDate)?.batches || [];
                if (!dayBatches.length) return <div className="p-4 text-zinc-600 text-xs">No production on this date</div>;
                return (
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-zinc-800/60">
                      {['Batch ID', 'Product', 'Batches', 'Output', 'Line', 'Status', 'Lots', ''].map(h => (
                        <th key={h} className="text-left text-zinc-500 text-[10px] font-medium px-3 py-2">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {dayBatches.map(b => (
                        <tr key={b.id} className="border-b border-zinc-800/30 hover:bg-zinc-800/20">
                          <td className="px-3 py-2 font-mono text-amber-400 font-bold cursor-pointer" onClick={() => { setTab('batches'); setTimeout(() => loadBatchDetail(b.id), 100); }}>{b.id}</td>
                          <td className="px-3 py-2 text-zinc-200"><CodeLabel name={b.product_name} /></td>
                          <td className="px-3 py-2 font-mono text-zinc-300">{b.num_batches}</td>
                          <td className="px-3 py-2 font-mono text-zinc-300">{b.total_output_kg} kg</td>
                          <td className="px-3 py-2 text-zinc-400">{b.production_line_name || b.production_line_code}</td>
                          <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full border text-[10px] ${BATCH_STYLES[b.status]}`}>{b.status}</span></td>
                          <td className="px-3 py-2 text-zinc-500">{b.materials_count > 0 ? `${b.materials_count} lots` : '-'}</td>
                          <td className="px-3 py-2">
                            {isAdmin && <button onClick={e => { e.stopPropagation(); requestDelete(`/production/batches/${b.id}`, b.id, `Delete batch ${b.id}. FIFO materials restored, FG stock reversed.`); }}
                              className="p-1 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30"><Trash2 size={11} /></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          )}
          {/* List fallback */}
          {!expandedDate && filteredTimeline.filter(d => d.date.startsWith(calMonth)).length === 0 && (
            <div className="text-center text-zinc-600 text-sm py-4">No production in {calMonth}{(batchSearch || clientFilter || productFilter || lineFilter !== 'All' || statusFilter !== 'All') ? ' matching your filters' : ''}</div>
          )}
        </div>
      )}


      {/* ══ HISTORY TAB ══ */}
      {tab === 'history' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-zinc-300 text-xs font-semibold">Completed Batches (older than 7 days)</span>
            <span className="text-zinc-500 text-[10px] font-mono">{filteredHistory.length}/{historyBatches.length} batches</span>
          </div>
          {historyLoading ? <div className="text-zinc-400 text-sm animate-pulse p-8 text-center">Loading...</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead><tr className="border-b border-zinc-800">
                  {['Batch ID', 'Product', 'Line', 'Output', 'Wastage', 'Client', 'Completed'].map(h => (
                    <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {filteredHistory.map(b => (
                    <tr key={b.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                      <td className="px-4 py-3 font-mono text-amber-400 text-xs font-semibold">{b.id}</td>
                      <td className="px-4 py-3 text-zinc-200 text-xs"><CodeLabel name={b.product_name} /></td>
                      <td className="px-4 py-3"><span className="text-[10px] px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: getLineColor(b.production_line_id) }}>{b.production_line_name}</span></td>
                      <td className="px-4 py-3 font-mono text-zinc-300 text-xs">{b.actual_output_kg || b.total_output_kg}kg</td>
                      <td className="px-4 py-3 font-mono text-xs">{b.wastage_kg > 0 ? <span className="text-red-400">{b.wastage_kg}kg ({b.wastage_pct}%)</span> : <span className="text-zinc-600">0</span>}</td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">{b.for_client || '—'}</td>
                      <td className="px-4 py-3 text-zinc-500 text-xs font-mono">{b.completed_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!historyLoading && historyBatches.length === 0 && (
            <div className="text-center py-12 text-zinc-600 text-xs">No completed batches older than 7 days</div>
          )}
          {!historyLoading && historyBatches.length > 0 && filteredHistory.length === 0 && (
            <div className="text-center py-12 text-zinc-600 text-xs">No batches match your filters</div>
          )}
        </div>
      )}


      {/* ══ RECIPES TAB ══ */}
      {tab === 'recipes' && (
        <div className="space-y-3">
          {!hasRecipeAccess ? (
            <div className="bg-zinc-900 border border-amber-900/40 rounded-xl p-8 text-center" data-testid="recipes-no-access">
              <FlaskConical size={32} className="text-amber-500/60 mx-auto mb-3" />
              <div className="text-amber-200 text-sm font-semibold">Recipes are restricted</div>
              <div className="text-zinc-500 text-xs mt-1">Ask an admin to grant you the <span className="font-mono bg-zinc-800 px-1.5 py-0.5 rounded">recipes</span> permission.</div>
            </div>
          ) : !recipeToken ? (
            <div className="bg-zinc-900 border border-amber-900/40 rounded-xl p-8 text-center" data-testid="recipes-locked">
              <FlaskConical size={32} className="text-amber-500 mx-auto mb-3" />
              <div className="text-amber-200 text-sm font-semibold">Recipes are password-protected</div>
              <div className="text-zinc-500 text-xs mt-1 mb-4">Enter the Recipes password to unlock for this session.</div>
              <button onClick={() => setShowRecipeAuth(true)} data-testid="unlock-recipes-btn"
                className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-4 py-2 rounded-lg">Unlock Recipes</button>
            </div>
          ) : (
          <ProtectedView label="RECIPES · CONFIDENTIAL">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-[280px]">
              <div className="relative flex-1 max-w-md">
                <input
                  value={recipeSearch}
                  onChange={e => setRecipeSearch(e.target.value)}
                  placeholder="Search by product name or code…"
                  data-testid="recipe-search"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-zinc-50 pl-8 focus:outline-none focus:border-amber-500"
                />
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              </div>
              <select value={recipeFilter} onChange={e => setRecipeFilter(e.target.value)}
                data-testid="recipe-filter"
                className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-[11px] text-zinc-200 focus:outline-none focus:border-amber-500">
                <option value="all">All recipes</option>
                <option value="with_code">With product code</option>
                <option value="without_code">Missing product code</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-green-400 font-mono bg-green-900/20 border border-green-700/40 px-2 py-0.5 rounded-full">🔓 Unlocked for session</span>
              <button onClick={lockRecipes} data-testid="lock-recipes-btn" className="text-[10px] text-zinc-500 hover:text-zinc-300 underline">Lock</button>
            </div>
          </div>
          {(() => {
            const q = recipeSearch.trim().toLowerCase();
            const filteredRecipes = recipes.filter(r => {
              if (recipeFilter === 'with_code' && !r.product_code) return false;
              if (recipeFilter === 'without_code' && r.product_code) return false;
              if (!q) return true;
              return (r.product_name || '').toLowerCase().includes(q) || (r.product_code || '').toLowerCase().includes(q);
            });
            if (recipes.length === 0) return (
              <div className="text-center text-zinc-500 text-sm py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
                <FlaskConical size={32} className="text-zinc-700 mx-auto mb-3" />
                <p>No recipes. Click "Add Recipe" to create one.</p>
              </div>
            );
            if (filteredRecipes.length === 0) return (
              <div className="text-center text-zinc-500 text-sm py-8 bg-zinc-900 border border-zinc-800 rounded-xl">
                No recipes match your filters · <button onClick={() => { setRecipeSearch(''); setRecipeFilter('all'); }} className="text-amber-400 hover:underline">clear</button>
              </div>
            );
            return filteredRecipes.map(r => (
            <div key={r.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`recipe-${r.product_id}`}>
              <div className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-600/10 border border-red-600/20 flex items-center justify-center"><FlaskConical size={15} className="text-red-400" /></div>
                  <div>
                    <div className="text-zinc-50 text-sm font-semibold flex items-center gap-1.5 flex-wrap"><ProductCodeBadge name={r.product_name} code={r.product_code} /><CodeLabel name={r.product_name} /></div>
                    <div className="text-zinc-500 text-xs">{r.materials?.length || 0} ingredients / {r.batch_size_kg ? `${r.batch_size_kg} kg` : '—'} per batch</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openEditRecipe(r)} className="text-xs text-zinc-400 hover:text-zinc-200 bg-zinc-800 px-2 py-1 rounded-lg border border-zinc-700" data-testid={`edit-recipe-${r.id}`}>Edit</button>
                  <button onClick={() => requestDelete(`/production/recipes/${r.id}`, r.product_name || r.id, `Delete recipe for "${r.product_name}". Batches using this recipe are not affected.`)} className="text-xs text-red-400 hover:text-red-300 bg-zinc-800 px-2 py-1 rounded-lg border border-zinc-700"><Trash2 size={11} /></button>
                </div>
              </div>
              {/* Ingredients table */}
              <div className="border-t border-zinc-800">
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-zinc-800/60">
                    {['#', 'Ingredient', 'Qty / Batch (kg)', 'Available Stock', 'Status'].map(h => (
                      <th key={h} className="text-left text-zinc-500 text-[10px] font-medium px-4 py-2">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {(r.materials || []).map((m, i) => {
                      const mat = materials.find(x => x.id === m.material_id);
                      const avail = mat?.current_stock || 0;
                      const qpb = m.quantity_per_batch || m.quantity_required || 0;
                      return (
                        <tr key={i} className="border-b border-zinc-800/30">
                          <td className="px-4 py-1.5 text-zinc-600">{i + 1}</td>
                          <td className="px-4 py-1.5 text-zinc-200"><ItemCodeLabel name={m.material_name} /></td>
                          <td className="px-4 py-1.5 font-mono text-zinc-300">{qpb} {m.unit || 'kg'}</td>
                          <td className="px-4 py-1.5 font-mono text-zinc-400">{avail} {mat?.unit || 'kg'}</td>
                          <td className="px-4 py-1.5">
                            {avail >= qpb
                              ? <span className="text-green-400 flex items-center gap-1"><CheckCircle2 size={11} /> OK</span>
                              : <span className="text-red-400 flex items-center gap-1"><AlertCircle size={11} /> Low</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="px-4 py-2 bg-zinc-800/20 border-t border-zinc-800/40 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span>
                    <span className="text-zinc-500 text-[10px]">Total per batch: </span>
                    <span className="font-mono text-zinc-300 text-[10px] font-semibold" data-testid={`recipe-total-${r.product_id}`}>
                      {(r.materials || []).reduce((s, m) => s + weightOf(m), 0).toFixed(2)} kg
                    </span>
                  </span>
                  {(r.materials || []).some(isPackagingLine) && (
                    <span className="text-[10px]" data-testid={`recipe-packing-${r.product_id}`}>
                      <span className="text-zinc-500">Packing (deducted per batch, not weight): </span>
                      <span className="font-mono text-amber-300">
                        {(r.materials || []).filter(isPackagingLine).map(m => `${m.material_name} ${m.quantity_per_batch || m.quantity_required || 0} ${m.unit || ''}`.trim()).join(' · ')}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          ));
          })()}
          </ProtectedView>
          )}
        </div>
      )}

      {/* ══ LINES & MAINTENANCE TAB ══ */}
      {tab === 'lines' && (
        <div className="space-y-3">
          {prodLines.map(line => {
            const lastMaint = line.last_maintenance;
            const interval = line.maintenance_interval_days || 30;
            const isDue = !lastMaint || (new Date() - new Date(lastMaint)) / 86400000 >= interval;
            return (
              <div key={line.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`line-${line.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-zinc-50 text-sm font-semibold">{line.name} <span className="font-mono text-zinc-500 text-xs">({line.code})</span></div>
                    <div className="text-zinc-500 text-xs mt-0.5">{line.description}</div>
                  </div>
                  <div className="text-right">
                    {isDue ? (
                      <span className="text-xs bg-red-900/40 text-red-300 border border-red-700 px-2 py-0.5 rounded-full">Maintenance Due</span>
                    ) : (
                      <span className="text-xs bg-green-900/40 text-green-300 border border-green-700 px-2 py-0.5 rounded-full">OK</span>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs">
                  <div className="text-zinc-400">
                    Last maintenance: <span className="font-mono text-zinc-300">{lastMaint || 'Never'}</span>
                  </div>
                  <div className="text-zinc-400">
                    Interval: <span className="font-mono text-zinc-300">{interval} days</span>
                  </div>
                  <button onClick={async () => {
                    await axios.patch(`${API}/production/lines/${line.id}/maintenance`, { date: new Date().toISOString().slice(0, 10) }, { withCredentials: true });
                    fetchData();
                    toast.success(`Maintenance recorded for ${line.name}`);
                  }} className="ml-auto text-xs bg-green-900/40 hover:bg-green-800/60 text-green-300 border border-green-700 px-3 py-1 rounded-lg transition-colors" data-testid={`maint-btn-${line.id}`}>
                    <Wrench size={11} className="inline mr-1" />Record Maintenance Done
                  </button>
                  {isAdmin && <button onClick={() => requestDelete(`/production/lines/${line.id}`, line.name, `Delete production line "${line.name}" permanently.`)}
                    data-testid={`delete-line-${line.id}`}
                    className="text-xs bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-800/30 px-3 py-1 rounded-lg transition-colors ml-2">
                    <Trash2 size={11} className="inline mr-1" />Delete Line
                  </button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══ CREATE BATCH MODAL ══ */}
      {showBatchModal && (
        <MobileModal onClose={() => { setShowBatchModal(false); setFeasibility(null); }} maxWidth="md" testId="batch-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50">Create Production Batch</h3>
              <button onClick={() => { setShowBatchModal(false); setFeasibility(null); }}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleCreateBatch} className="p-5 space-y-4" data-testid="batch-form">
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">Recipe / Product *</label>
                <select required value={batchForm.product_id} onChange={e => setBatchForm(f => ({ ...f, product_id: e.target.value }))}
                  data-testid="batch-product-select"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-red-500">
                  <option value="">Select recipe...</option>
                  {recipes.map(r => (
                    <option key={r.product_id} value={r.product_id} className="bg-zinc-900">
                      {r.product_code ? `[${r.product_code}] ` : ''}{r.product_name} ({r.batch_size_kg ? `${r.batch_size_kg} kg/batch` : 'no recipe size'})
                    </option>
                  ))}
                </select>
                {batchForm.product_id && recipesForProduct[batchForm.product_id] && (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <ProductCodeBadge
                        name={recipesForProduct[batchForm.product_id].product_name}
                        code={recipesForProduct[batchForm.product_id].product_code}
                      />
                      <span className="text-zinc-500">product code (client-facing ID)</span>
                    </div>
                    {(() => {
                      const rec = recipesForProduct[batchForm.product_id];
                      const perBatch = (rec?.materials || []).reduce((a, m) => a + weightOf(m), 0) || rec?.batch_size_kg || 0;
                      const n = Math.max(1, parseInt(batchForm.num_batches || '1'));
                      return (
                        <div className="bg-zinc-950 border border-amber-700/40 rounded-lg px-3 py-2 flex items-center gap-3 text-[11px]">
                          <div className="text-zinc-500">Per batch:</div>
                          <div className="text-amber-300 font-mono font-semibold" data-testid="batch-per-output">{perBatch.toFixed(2)} kg</div>
                          <div className="text-zinc-600">×</div>
                          <div className="text-zinc-300 font-mono">{n}</div>
                          <div className="text-zinc-600">=</div>
                          <div className="text-green-400 font-mono font-bold" data-testid="batch-total-output">{(perBatch * n).toFixed(2)} kg total</div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">No. of Batches *</label>
                  <input required type="number" min="1" value={batchForm.num_batches}
                    onChange={e => setBatchForm(f => ({ ...f, num_batches: e.target.value }))}
                    data-testid="batch-qty-input"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-red-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Production Line</label>
                  <select value={batchForm.production_line_id} onChange={e => setBatchForm(f => ({ ...f, production_line_id: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-red-500">
                    <option value="">Select line...</option>
                    {prodLines.map(l => <option key={l.id} value={l.id} className="bg-zinc-900">{l.name} ({l.code})</option>)}
                  </select>
                </div>
              </div>
              {/* iter185 — Production Date (defaults to today; backdating supported) */}
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">Production Date <span className="text-zinc-600">(used for batch ID YYMMDD)</span></label>
                <input type="date"
                  value={batchForm.production_date || ''}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={e => setBatchForm(f => ({ ...f, production_date: e.target.value }))}
                  data-testid="production-date-input"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-red-500" />
              </div>
              {batchForm.product_id && batchForm.num_batches && (
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-3 text-xs">
                  <p className="text-zinc-300 font-semibold">Output: {(recipesForProduct[batchForm.product_id]?.batch_size_kg || 0) * parseInt(batchForm.num_batches || 1)} kg</p>
                  {/* iter185 — Auto Batch ID preview using new simple format: [LETTER]YYMMDD */}
                  <p className="text-zinc-500 mt-0.5">
                    Auto Batch ID:{' '}
                    <span className="font-mono text-zinc-300">
                      {(() => {
                        const d = (batchForm.production_date || new Date().toISOString().slice(0, 10)).slice(2, 10).replace(/-/g, '');
                        const n = Math.max(1, parseInt(batchForm.num_batches || '1'));
                        const ids = Array.from({ length: Math.min(n, 3) }, (_, i) => String.fromCharCode(65 + i) + d);
                        return ids.join(', ') + (n > 3 ? `, … (${n} total)` : '');
                      })()}
                    </span>
                  </p>
                </div>
              )}
              {/* Batch ID Manual Override (iter185 — multi-batch now supported when in simple format) */}
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">
                  Batch ID Override <span className="text-zinc-600">(leave blank for auto · used as FIRST id when n {'>'} 1)</span>
                </label>
                <input value={batchForm.batch_id_override || ''} onChange={e => setBatchForm(f => ({ ...f, batch_id_override: e.target.value.toUpperCase() }))}
                  data-testid="batch-id-override-input"
                  placeholder="e.g., A260403 → A260403, B260403, C260403…"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-red-500 font-mono" />
              </div>
              {/* Pre-check that matches reality: needed · total · in lots, colour-coded */}
              {feasibility && (
                <div className={`rounded-xl border text-xs overflow-hidden ${feasibility.shortages?.length ? 'border-red-800/70 bg-red-950/20' : feasibility.unlotted?.length ? 'border-amber-700/70 bg-amber-950/20' : 'border-emerald-800/70 bg-emerald-950/20'}`} data-testid="batch-precheck">
                  <div className="px-3 py-2 flex items-center justify-between gap-2">
                    <p className={`font-semibold ${feasibility.shortages?.length ? 'text-red-300' : feasibility.unlotted?.length ? 'text-amber-300' : 'text-emerald-300'}`}>
                      {feasibility.shortages?.length ? 'Total stock is short for some materials' : feasibility.unlotted?.length ? 'Total covers it — lots don\u2019t (lot entry pending)' : 'All materials available in lots'}
                    </p>
                    <span className="text-[10px] text-zinc-500 whitespace-nowrap">● lots cover · <span className="text-amber-400">●</span> total only · <span className="text-red-400">●</span> short</span>
                  </div>
                  {Array.isArray(feasibility.materials) && feasibility.materials.length > 0 && (
                    <div className="max-h-36 overflow-y-auto border-t border-zinc-800/60">
                      <table className="w-full text-[11px]">
                        <thead className="text-[10px] text-zinc-500 bg-zinc-950/40">
                          <tr><th className="text-left px-3 py-1 font-medium">Material</th><th className="text-right px-2 py-1 font-medium">Need</th><th className="text-right px-2 py-1 font-medium">Total</th><th className="text-right px-3 py-1 font-medium">In lots</th></tr>
                        </thead>
                        <tbody>
                          {feasibility.materials.map((m) => (
                            <tr key={m.material_id} className="border-t border-zinc-800/40" data-testid={`precheck-row-${m.material_id}`}>
                              <td className="px-3 py-1 text-zinc-200 flex items-center gap-1.5">
                                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${m.status === 'short' ? 'bg-red-400' : m.status === 'unlotted' ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                                <ItemCodeLabel name={m.material_name} />
                              </td>
                              <td className="px-2 py-1 text-right font-mono text-zinc-100">{m.required}</td>
                              <td className={`px-2 py-1 text-right font-mono ${m.status === 'short' ? 'text-red-300' : 'text-zinc-300'}`}>{m.total_stock}</td>
                              <td className={`px-3 py-1 text-right font-mono ${m.status === 'unlotted' ? 'text-amber-300' : 'text-zinc-300'}`}>{m.in_lots}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">Producing For</label>
                <select value={batchForm.for_client} onChange={e => setBatchForm(f => ({ ...f, for_client: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-red-500">
                  <option value="General Stock">General Stock (maintain inventory)</option>
                  {clients.map(c => <option key={c} value={c} className="bg-zinc-900">{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">Notes</label>
                <input value={batchForm.notes} onChange={e => setBatchForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-red-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowBatchModal(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-semibold text-sm disabled:opacity-50">{submitting ? 'Creating...' : 'Create Batch'}</button>
              </div>
            </form>
        </MobileModal>
      )}

      {/* ══ RECIPE MODAL ══ */}
      {showRecipeModal && (
        <MobileModal onClose={() => { setShowRecipeModal(false); setEditRecipe(null); }} maxWidth="2xl" testId="recipe-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10 rounded-t-2xl">
              <h3 className="font-heading font-semibold text-zinc-50">{editRecipe ? 'Edit Recipe' : 'Create Recipe'}</h3>
              <div className="flex items-center gap-2">
                {editRecipe && isAdmin && (
                  <button
                    type="button"
                    onClick={() => { setShowVersionsRecipe(editRecipe); }}
                    data-testid="recipe-versions-btn"
                    className="text-xs px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 flex items-center gap-1.5"
                    title="View edit history / diff / revert"
                  >
                    <History size={12} /> Versions
                  </button>
                )}
                <button onClick={() => { setShowRecipeModal(false); setEditRecipe(null); }}><X size={18} className="text-zinc-500" /></button>
              </div>
            </div>
            <form onSubmit={handleSaveRecipe} className="p-5 space-y-4" data-testid="recipe-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Recipe / Product Name *</label>
                  {editRecipe ? (
                    <input value={recipeForm.product_name} readOnly className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-400 text-sm" />
                  ) : (
                    <select required value={recipeForm.product_id}
                      onChange={e => {
                        const p = products.find(x => x.id === e.target.value);
                        setRecipeForm(f => ({ ...f, product_id: e.target.value, product_name: p?.name || '' }));
                      }}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-red-500">
                      <option value="">Select product...</option>
                      {products.filter(p => !recipesForProduct[p.id]).map(p => (
                        <option key={p.id} value={p.id} className="bg-zinc-900">{p.name}</option>
                      ))}
                      {/* Also allow selecting products that already have recipes (for re-creation) */}
                      {products.filter(p => recipesForProduct[p.id]).map(p => (
                        <option key={p.id} value={p.id} className="bg-zinc-900">{p.name} (update existing)</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block flex items-center gap-1.5">
                    Batch Size (kg) <span className="text-[9px] text-zinc-600">auto = sum of ingredients</span>
                  </label>
                  <input
                    readOnly
                    value={(recipeForm.materials || []).reduce((acc, m) => acc + weightOf(m), 0).toFixed(2)}
                    data-testid="recipe-computed-size"
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 text-amber-300 font-mono text-sm font-semibold cursor-not-allowed"
                    title="Automatically computed from material quantities below"
                  />
                </div>
              </div>

              {/* Product Code (client-facing recipe variant ID) */}
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block flex items-center gap-1.5">
                  Product Code <span className="text-zinc-600">(pick from registry — e.g. TI-151 or TI-151-T2)</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={recipeForm.product_code || ''}
                    onChange={e => setRecipeForm(f => ({ ...f, product_code: e.target.value }))}
                    data-testid="recipe-product-code-select"
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500 font-mono">
                    <option value="">— No product code —</option>
                    {Object.entries(productCodesMap).map(([realName, v]) => (
                      <option key={v.code} value={v.code} className="bg-zinc-900">
                        [{v.code}] {realName}
                      </option>
                    ))}
                  </select>
                  {recipeForm.product_code && (
                    <div className="flex items-center px-2"><ProductCodeBadge code={recipeForm.product_code} /></div>
                  )}
                </div>
                <p className="text-[10px] text-zinc-600 mt-1">
                  Manage codes in <span className="text-amber-300 font-semibold">Inventory → Product Codes</span>. This ID propagates to Batches, Kanban, Dispatch, Enterprise bills & the Customer Portal.
                </p>
              </div>

              {/* Ingredients Table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-zinc-400 text-xs font-semibold">Ingredients</label>
                  <button type="button" onClick={addIngredientRow} className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 px-2 py-1 rounded-lg">
                    <Plus size={11} className="inline mr-1" />Add Row
                  </button>
                </div>
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl overflow-hidden">
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-zinc-700">
                      {['#', 'Raw Material', 'Qty per Batch (kg)', 'Unit', 'Phase', ''].map(h => (
                        <th key={h} className="text-left text-zinc-500 text-[10px] font-medium px-3 py-2">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {recipeForm.materials.map((m, i) => (
                        <tr key={`recipe-mat-${i}-${m.material_id || 'new'}`} className="border-b border-zinc-700/50">
                          <td className="px-3 py-1.5 text-zinc-600 w-8">{i + 1}</td>
                          <td className="px-3 py-1.5">
                            <select value={m.material_id} onChange={e => updateIngredient(i, 'material_id', e.target.value)}
                              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-50 text-xs focus:outline-none focus:border-red-500">
                              <option value="">Select...</option>
                              {materials.map(mat => {
                                const code = isAdmin ? lookupItemCode(mat.name) : '';
                                return <option key={mat.id} value={mat.id} className="bg-zinc-900">{code ? `${code} · ${mat.name}` : mat.name}</option>;
                              })}
                            </select>
                          </td>
                          <td className="px-3 py-1.5">
                            <input type="number" step="any" min="0" value={m.quantity_per_batch}
                              onChange={e => updateIngredient(i, 'quantity_per_batch', e.target.value)}
                              placeholder="0.00"
                              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-50 text-xs font-mono focus:outline-none focus:border-red-500" />
                          </td>
                          <td className="px-3 py-1.5">
                            <select value={m.unit} onChange={e => updateIngredient(i, 'unit', e.target.value)}
                              className="bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-50 text-xs focus:outline-none">
                              {['kg', 'g', 'litre', 'ml', 'pcs'].map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-1.5">
                            {/* iter204 — Phase controls which weighing section
                                this material lands in on the printed Batch Sheet.
                                iter206 — Labels match the physical sheet wording
                                (Before / During / After Production Process). */}
                            <select value={m.phase || 'premix'}
                              onChange={e => updateIngredient(i, 'phase', e.target.value)}
                              data-testid={`recipe-phase-${i}`}
                              title="Weighing phase (groups rows on the printed Batch Sheet)"
                              className="bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-50 text-[10px] focus:outline-none">
                              <option value="premix" className="bg-zinc-900">Before Process</option>
                              <option value="during_process" className="bg-zinc-900">During Process</option>
                              <option value="after_process" className="bg-zinc-900">After Process</option>
                              {/* iter238 — packaging is consumed with the batch and
                                  its cost counts, but it is not product weight: no
                                  effect on batch size, hidden on the printed sheet. */}
                              <option value="packaging" className="bg-zinc-900">Packaging (no weight)</option>
                            </select>
                          </td>
                          <td className="px-3 py-1.5 w-8">
                            {recipeForm.materials.length > 1 && (
                              <button type="button" onClick={() => removeIngredientRow(i)} className="text-red-400 hover:text-red-300">
                                <Trash2 size={12} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-3 py-2 bg-zinc-800/30 border-t border-zinc-700/50 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span>
                      <span className="text-zinc-500 text-[10px]">Batch size (ingredients): </span>
                      <span className="font-mono text-zinc-300 text-[10px] font-semibold" data-testid="recipe-ingredient-total">
                        {recipeForm.materials.reduce((s, m) => s + weightOf(m), 0).toFixed(3)} kg
                      </span>
                    </span>
                    {recipeForm.materials.some(isPackagingLine) && (
                      <span data-testid="recipe-packaging-total">
                        <span className="text-zinc-500 text-[10px]">Packing / pcs lines (excluded from weight, deducted & costed): </span>
                        <span className="font-mono text-amber-300 text-[10px] font-semibold">
                          {recipeForm.materials.filter(isPackagingLine).map(m => `${parseFloat(m.quantity_per_batch) || 0} ${m.unit || ''}`.trim()).join(' + ')}
                        </span>
                      </span>
                    )}
                    <span className="text-zinc-600 text-[10px]">Packaging lines never print on the batch sheet.</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">Notes</label>
                <input value={recipeForm.notes} onChange={e => setRecipeForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-red-500" />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowRecipeModal(false); setEditRecipe(null); }} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                  <Save size={14} />{submitting ? 'Saving...' : editRecipe ? 'Update Recipe' : 'Save Recipe'}
                </button>
              </div>
            </form>
        </MobileModal>
      )}
      {/* ═══ TIMING / DETAILS EDITOR MODAL ═══ */}
      {editTimings && (
        <TimingEditor
          batch={editTimings}
          isAdmin={isAdmin}
          onClose={() => setEditTimings(null)}
          onSave={(id, data) => { updateBatchDetails(id, data); setEditTimings(null); }}
          onRenamed={() => { setEditTimings(null); fetchData(); }}
        />
      )}

      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />

      {/* ══ MATERIALS NOT IN STOCK (lots) — unlotted + stock-debt confirmation ══ */}
      {stockConfirm && (() => {
        const shorts = stockConfirm.shortages || [];
        const unl = stockConfirm.unlotted || [];
        const canConfirm = shorts.length === 0 || isAdmin;
        return (
          <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4" data-testid="rm-shortage-override-modal">
            <div className={`bg-zinc-900 border rounded-2xl w-full max-w-lg p-5 space-y-4 shadow-2xl ${shorts.length ? 'border-red-700/60' : 'border-amber-600/60'}`}>
              <div className="flex items-center gap-2">
                <AlertTriangle size={20} className={shorts.length ? 'text-red-400' : 'text-amber-400'} />
                <h3 className="text-zinc-50 font-semibold text-base">These materials are not in stock (lots)</h3>
              </div>
              {unl.length > 0 && (
                <p className="text-zinc-400 text-xs leading-relaxed">
                  <span className="text-amber-300 font-semibold">Amber</span> — the total stock covers the need but no lot has it yet (purchase entry pending).
                  Proceeding consumes whatever lots exist and records the rest as <span className="text-amber-300 font-semibold">UNLOTTED</span>; totals are reduced as usual and you assign the real lot later from the batch.
                </p>
              )}
              {shorts.length > 0 && (
                <p className="text-zinc-400 text-xs leading-relaxed">
                  <span className="text-red-300 font-semibold">Red</span> — the total itself is short. {isAdmin ? <>As <span className="text-red-300 font-semibold">owner/admin</span> you may allow a <span className="text-red-300 font-semibold">stock debt</span>: what exists is consumed now, the shortfall is recorded as pending and settles automatically from the next inward of that material.</> : <>Ask an owner/admin to allow stock debt for this batch.</>}
                </p>
              )}
              <div className="border border-zinc-800 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-950 text-zinc-500 uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="text-left px-3 py-2 whitespace-nowrap">Material</th>
                      <th className="text-right px-2 py-2 whitespace-nowrap">Need</th>
                      <th className="text-right px-2 py-2 whitespace-nowrap">Total</th>
                      <th className="text-right px-2 py-2 whitespace-nowrap">In lots</th>
                      <th className="text-right px-3 py-2 whitespace-nowrap">Short by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unl.map((s, i) => (
                      <tr key={`u${i}`} className="border-t border-zinc-800" data-testid={`rm-unlotted-row-${i}`}>
                        <td className="px-3 py-2 text-zinc-100 font-medium"><span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 mr-1.5 align-middle" /><ItemCodeLabel name={s.material_name} /></td>
                        <td className="px-2 py-2 text-zinc-300 font-mono text-right">{s.required} {s.unit}</td>
                        <td className="px-2 py-2 text-zinc-300 font-mono text-right">{s.available}</td>
                        <td className="px-2 py-2 text-amber-300 font-mono text-right">{s.in_lots}</td>
                        <td className="px-3 py-2 text-amber-300 font-mono text-right">UNLOTTED {s.unlotted_qty}</td>
                      </tr>
                    ))}
                    {shorts.map((s, i) => (
                      <tr key={`s${i}`} className="border-t border-zinc-800" data-testid={`rm-short-row-${i}`}>
                        <td className="px-3 py-2 text-zinc-100 font-medium"><span className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 mr-1.5 align-middle" /><ItemCodeLabel name={s.material_name} /></td>
                        <td className="px-2 py-2 text-zinc-300 font-mono text-right">{s.required} {s.unit}</td>
                        <td className="px-2 py-2 text-red-300 font-mono text-right">{s.available}</td>
                        <td className="px-2 py-2 text-zinc-400 font-mono text-right">{s.in_lots}</td>
                        <td className="px-3 py-2 text-red-300 font-mono font-semibold text-right">{s.short_by} {s.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setStockConfirm(null)} data-testid="rm-shortage-cancel"
                  className="flex-1 py-2 rounded-xl border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs font-medium">
                  Cancel
                </button>
                {canConfirm ? (
                  <button onClick={() => handleCreateBatch(null, { allowUnlotted: true, allowShortage: shorts.length > 0 })}
                    disabled={submitting} data-testid="rm-shortage-override-confirm"
                    className={`flex-1 py-2 rounded-xl font-semibold text-xs disabled:opacity-50 ${shorts.length ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-amber-500 hover:bg-amber-400 text-black'}`}>
                    {submitting ? 'Creating…' : shorts.length ? (unl.length ? 'Allow stock debt + record UNLOTTED' : 'Allow stock debt & create') : 'Proceed — record as UNLOTTED'}
                  </button>
                ) : (
                  <div className="flex-1 py-2 rounded-xl border border-red-800/60 text-red-300 text-xs text-center" data-testid="rm-shortage-needs-admin">
                    Ask an owner/admin to allow stock debt
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ RECIPE PASSWORD MODAL ══ */}
      {showRecipeAuth && hasRecipeAccess && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="recipe-auth-modal">
          <div className="bg-zinc-900 border border-amber-900/40 rounded-2xl w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center gap-2">
              <FlaskConical size={18} className="text-amber-500" />
              <h3 className="text-zinc-50 font-semibold text-base">Recipes are locked</h3>
            </div>
            <p className="text-zinc-400 text-xs">Enter the Recipes password to unlock for this session. Required for view / edit / delete.</p>
            <input type="password" autoFocus value={recipePwd} onChange={e => setRecipePwd(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && verifyRecipePassword()}
              data-testid="recipe-password-input"
              placeholder="Recipes password"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            {recipeAuthErr && <div className="text-red-400 text-xs">{recipeAuthErr}</div>}
            <div className="flex gap-2">
              <button onClick={() => { setShowRecipeAuth(false); setRecipePwd(''); setRecipeAuthErr(''); if (tab === 'recipes') setTab('kanban'); }}
                className="flex-1 py-2 rounded-xl border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-xs font-medium">Cancel</button>
              <button onClick={verifyRecipePassword} disabled={recipeAuthLoading || !recipePwd}
                data-testid="recipe-password-submit"
                className="flex-1 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs disabled:opacity-50">
                {recipeAuthLoading ? 'Verifying…' : 'Unlock'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showVersionsRecipe && (
        <RecipeVersionsModal
          recipe={showVersionsRecipe}
          recipeToken={recipeToken}
          onClose={() => setShowVersionsRecipe(null)}
          onReverted={() => { setShowVersionsRecipe(null); fetchData(); }}
        />
      )}

      {/* Stage 0 — open the batch picked from the search dropdown */}
      {openBatch && (
        <BatchGroupDetailModal
          group={{ batch_ids: [openBatch.id], product_name: openBatch.product_name, for_client: openBatch.for_client, batches_count: 1, line_code: openBatch.production_line_code }}
          dayDate={openBatch.production_date || (openBatch.created_at || '').slice(0, 10)}
          isAdmin={isAdmin}
          onClose={() => { setOpenBatch(null); fetchData(); }}
        />
      )}

      {/* iter204 — Printable Batch Sheet modal */}
      {batchSheetBatchId && (
        <BatchSheetModal
          batchId={batchSheetBatchId}
          onClose={() => setBatchSheetBatchId(null)}
        />
      )}

      {/* iter217 — Overhead allocation modal (Phase 2) */}
      {allocateBatch && (
        <AllocateOverheadModal
          batch={allocateBatch}
          onClose={() => setAllocateBatch(null)}
          onAllocated={async () => { setAllocateBatch(null); await fetchData(); }}
        />
      )}
    </div>
  );
}

// ─── iter217 — Overhead Allocation Modal (Phase 2) ──────────────────────────
// Lets an admin stamp the equal-per-kg overhead onto a batch. Backend
// computes the suggested ₹/kg rate from the trailing 30-day overhead
// expense pool divided by total batch output. Admin can override.
function AllocateOverheadModal({ batch, onClose, onAllocated }) {
  const [rate, setRate] = useState(null);
  const [days, setDays] = useState(30);
  const [override, setOverride] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const output_kg = Number(batch.actual_output_kg || batch.total_output_kg || batch.batch_size_kg || 0);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/expenses/overhead-rate`, {
          params: { days },
          withCredentials: true,
        });
        if (active) setRate(r.data);
      } catch (e) {
        if (active) toast.error('Could not load overhead rate');
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [days]);

  const effectiveRate = override !== '' ? parseFloat(override) : (rate?.rate_per_kg || 0);
  const projectedOverhead = effectiveRate * output_kg;
  const projectedTotal = (batch.rm_cost_actual || 0) + projectedOverhead;
  const projectedPerKg = output_kg > 0 ? projectedTotal / output_kg : 0;

  const allocate = async () => {
    if (!effectiveRate || effectiveRate <= 0) {
      toast.error('Rate must be > 0');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/expenses/allocate-to-batch/${batch.id}`, {
        rate: effectiveRate,
        basis: 'per_kg',
        days,
      }, { withCredentials: true });
      toast.success('Overhead allocated');
      onAllocated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to allocate');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="allocate-overhead-modal">
      <div className="bg-zinc-900 border border-emerald-700/50 rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div>
            <h3 className="font-heading font-semibold text-zinc-50">Allocate Overhead</h3>
            <div className="text-zinc-500 text-xs mt-0.5 font-mono">{batch.id} · {batch.product_name}</div>
          </div>
          <button onClick={onClose}><X size={18} className="text-zinc-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-xs">
            <div className="flex items-center justify-between text-zinc-400 mb-1">
              <span>Batch output</span>
              <span className="font-mono text-zinc-200">{output_kg} kg</span>
            </div>
            <div className="flex items-center justify-between text-zinc-400 mb-1">
              <span>RM cost (FIFO)</span>
              <span className="font-mono text-amber-300">{fmt(batch.rm_cost_actual)}</span>
            </div>
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Look-back window</label>
            <div className="flex gap-2">
              {[7, 30, 60, 90].map(d => (
                <button key={d} onClick={() => { setDays(d); setOverride(''); }}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${days === d ? 'bg-amber-500 text-black font-semibold' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
                  {d}d
                </button>
              ))}
            </div>
          </div>
          {loading ? (
            <div className="text-zinc-500 text-xs animate-pulse text-center py-3">Computing rate…</div>
          ) : (
            <div className="bg-emerald-900/15 border border-emerald-800/40 rounded-xl p-3 space-y-1.5 text-xs">
              <div className="flex items-center justify-between text-emerald-300 font-semibold">
                <span>Suggested ₹/kg</span>
                <span className="font-mono">₹{rate?.rate_per_kg || 0}</span>
              </div>
              <div className="flex items-center justify-between text-zinc-500">
                <span>Overhead pool ({days}d)</span>
                <span className="font-mono">{fmt(rate?.overhead_total)}</span>
              </div>
              <div className="flex items-center justify-between text-zinc-500">
                <span>Output basis ({days}d)</span>
                <span className="font-mono">{rate?.output_kg || 0} kg</span>
              </div>
            </div>
          )}
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Override rate (optional)</label>
            <input type="number" step="0.01" min="0" value={override}
              onChange={e => setOverride(e.target.value)} placeholder="leave blank to use suggested"
              data-testid="overhead-rate-override"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:border-amber-500 focus:outline-none" />
          </div>
          <div className="bg-zinc-950 border border-amber-700/40 rounded-xl p-3 text-xs">
            <div className="text-zinc-400 mb-1">Projection</div>
            <div className="flex items-center justify-between text-zinc-300">
              <span>+ Overhead at ₹{effectiveRate.toFixed(2)}/kg × {output_kg} kg</span>
              <span className="font-mono text-emerald-300">{fmt(projectedOverhead)}</span>
            </div>
            <div className="flex items-center justify-between text-zinc-100 font-semibold mt-1 pt-1 border-t border-zinc-800">
              <span>= Total cost</span>
              <span className="font-mono text-amber-300">{fmt(projectedTotal)}</span>
            </div>
            <div className="text-zinc-500 text-[10px] mt-0.5">→ ₹{projectedPerKg.toFixed(2)}/kg full</div>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
            <button onClick={allocate} disabled={busy || loading} data-testid="confirm-allocate"
              className="flex-1 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm disabled:opacity-50">
              {busy ? 'Allocating…' : 'Stamp Overhead'}
            </button>
          </div>
        </div>
      </div>

      {/* iter238 · Packaging re-tagging review */}
      {pkgScan && pkgScan.count > 0 && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="pkg-scan-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-zinc-800">
              <p className="font-heading text-zinc-50 text-sm">Packaging lines found in your recipes</p>
              <p className="text-[11px] text-zinc-500 mt-0.5">{pkgScan.note}</p>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1.5">
              {pkgScan.hits.map((h, i) => (
                <label key={`${h.recipe_id}-${h.material_id}-${i}`}
                  data-testid={`pkg-scan-row-${i}`}
                  className="flex items-center gap-3 bg-zinc-800/40 border border-zinc-800 rounded-lg px-3 py-2 cursor-pointer">
                  <input type="checkbox" checked={!!pkgPicks[i]} className="accent-amber-500"
                    onChange={e => setPkgPicks(p => ({ ...p, [i]: e.target.checked }))} />
                  <span className="flex-1 text-[12px] text-zinc-100">
                    {h.material_name}
                    <span className="text-zinc-500"> · {h.quantity_per_batch} {h.unit} in </span>
                    <span className="text-zinc-300">{h.product_name}</span>
                  </span>
                  <span className="text-[10px] text-zinc-500">matched “{h.matched_on}”</span>
                  <span className="text-[10px] text-zinc-500 font-mono">batch {h.current_batch_size_kg} kg</span>
                </label>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-zinc-800 flex items-center gap-2">
              <span className="text-[11px] text-zinc-500 flex-1">
                {Object.values(pkgPicks).filter(Boolean).length} of {pkgScan.count} selected
              </span>
              <button onClick={() => setPkgScan(null)} data-testid="pkg-scan-cancel"
                className="text-[12px] text-zinc-400 hover:text-zinc-200 px-3 py-1.5">Cancel</button>
              <button onClick={applyPackaging} disabled={pkgBusy} data-testid="pkg-scan-apply"
                className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-[12px] font-semibold px-3.5 py-1.5 rounded-lg">
                {pkgBusy ? 'Moving…' : 'Move to Packaging'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

